import { Controller } from "@hotwired/stimulus";

interface ButtonClickedEvent extends Event {
  params: {
    value: CategoryValue;
  };
}

type CategoryValue = {
  id: string;
  icon: string;
  name: string;
};

export default class extends Controller<HTMLElement> {
  static targets = [
    "option",
    "modal",
    "button",
    "placeholder",
    "select",
    "icon",
  ];

  static values = {
    selected: Object,
  };

  declare selectedValue: CategoryValue;
  declare readonly hasSelectedValue: boolean;

  declare readonly hasButtonTarget: boolean;
  declare readonly buttonTarget: HTMLButtonElement;
  declare readonly buttonTargets: HTMLButtonElement[];

  declare readonly hasPlaceholderTarget: boolean;
  declare readonly placeholderTarget: HTMLElement;
  declare readonly placeholderTargets: HTMLElement[];

  declare readonly hasIconTarget: boolean;
  declare readonly iconTarget: HTMLImageElement;
  declare readonly iconTargets: HTMLImageElement[];

  declare readonly hasOptionTarget: boolean;
  declare readonly optionTarget: HTMLOptionElement;
  declare readonly optionTargets: HTMLOptionElement[];

  declare readonly hasModalTarget: boolean;
  declare readonly modalTarget: HTMLDialogElement;
  declare readonly modalTargets: HTMLDialogElement[];

  declare readonly hasSelectTarget: boolean;
  declare readonly selectTarget: HTMLSelectElement;
  declare readonly selectTargets: HTMLSelectElement[];

  connect(): void {
    this.selectTarget.form?.addEventListener("reset", () =>
      setTimeout(() => {
        this.selectValueChanged();
      }),
    );

    // Open the modal when the placeholder is clicked if the value is uncategorised
    const src = this.element.closest("turbo-frame").getAttribute("src");
    if (
      src &&
      new URL(src as string).searchParams.get("target") === "category" &&
      this.selectTarget.value ===
        window.BudgeterConfig.uncategorised_category_id
    ) {
      this.showCategoriesSelector();
    }
  }

  showCategoriesSelector() {
    this.modalTarget.showModal();
    this.buttonTargets
      .find((element) => element.hasAttribute("data-categories-selected"))
      ?.focus();
  }

  hideCategoriesSelector() {
    this.modalTarget.close();
  }

  refreshState() {
    // Set selected values
    this.optionTargets.forEach((element) => {
      element.selected = element.value == this.selectedValue.id;
    });

    // Apply placeholder values
    this.iconTarget.src = this.selectedValue.icon;
    this.placeholderTarget.innerText = this.selectedValue.name;

    // Set buttons active states
    this.buttonTargets.forEach((element) => {
      element.toggleAttribute(
        "data-categories-selected",
        element.dataset.id == this.selectedValue.id,
      );
    });
  }

  selectValueChanged() {
    let selectedId = "";
    this.optionTargets.forEach((element) => {
      if (element.selected) {
        selectedId = element.value;
      }
    });

    this.buttonTargets.forEach((element) => {
      if (element.dataset.id == selectedId) {
        const value = JSON.parse(
          element.dataset.categoriesValueParam as string,
        ) as CategoryValue;
        this.selectedValue = value;
      }
    });
  }

  selectedValueChanged() {
    if (this.hasSelectedValue) {
      this.refreshState();
      this.selectTarget.form?.dispatchEvent(
        new Event("budgeter:category-changed"),
      );
    }
  }

  buttonClicked(event: ButtonClickedEvent) {
    this.selectedValue = event.params.value;
    this.hideCategoriesSelector();
  }
}
